import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { updateForeclosureCaseReview } from "@/hera-api/foreclosure-license-link-api";
import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";
import {
  applyForeclosureCaseReviewToCachedLists,
  startDateOverrideCachePatch,
} from "@/lib/foreclosure-case-query-invalidation";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useState } from "react";

interface ForeclosureCaseStartDateEditCellProps {
  row: GovernmentForeclosureCaseListItem;
  portal: "government" | "operator";
}

/**
 * Lets an operator (or government user, matching the OMT/AMS/registrant
 * party cells) set or clear a manual start date, overriding whatever ATTOM
 * loaded. The Athena nightly rebuild never touches this override — see
 * `override_start_date` on the review row.
 */
export function ForeclosureCaseStartDateEditCell({
  row,
  portal,
}: ForeclosureCaseStartDateEditCellProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const effectiveStartDate = row.effective_start_date ?? row.start_date ?? null;
  const isOverridden = row.is_start_date_overridden ?? false;
  const [draftDate, setDraftDate] = useState<string>(effectiveStartDate ?? "");

  const saveMutation = useMutation({
    mutationFn: async (nextDate: string | null) =>
      updateForeclosureCaseReview(portal, {
        countyFips: row.county_fips,
        caseNumber: row.case_number,
        overrideStartDate: nextDate,
      }),
    onSuccess: (review) => {
      applyForeclosureCaseReviewToCachedLists(
        queryClient,
        review,
        startDateOverrideCachePatch(row, review),
      );
      setOpen(false);
      toast({
        title: "Start date saved",
        variant: "secondary",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to save start date",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftDate(effectiveStartDate ?? "");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div
        className="flex items-center gap-1"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <span>
          {effectiveStartDate ?? "—"}
          {isOverridden ? (
            <span
              className="ml-1 text-xs text-muted-foreground"
              title="Manually overridden; the system-loaded date is kept separately."
            >
              (manual)
            </span>
          ) : null}
        </span>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            aria-label={`Edit start date for case ${row.case_number}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="w-64 space-y-3"
        align="start"
        onClick={(event) => event.stopPropagation()}
      >
        <Input
          type="date"
          value={draftDate}
          onChange={(event) => setDraftDate(event.target.value)}
          aria-label="Manual start date"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={saveMutation.isPending || !draftDate}
            onClick={() => saveMutation.mutate(draftDate)}
          >
            Save
          </Button>
          {isOverridden ? (
            <Button
              size="sm"
              variant="outline"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(null)}
            >
              Clear override
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
